import React from 'react';
import { FormRenderProps } from '@progress/kendo-react-form';
import { Grid, GridColumn, GridItemChangeEvent } from '@progress/kendo-react-grid';
import { Tooltip } from '@progress/kendo-react-tooltip';
import { EditDescriptor } from '@progress/kendo-react-data-tools';
import { createCustomFieldCellWrapper, createRemoveCellWrapper, NameCell } from './FormFileMetadataCells';
import {
  useCurrentUserContext,
  useGlobalizationContext,
  useGetAllFields,
  useTranslation
} from '@cs-cms/state-management';
import { ContentTypeFieldResponse, CPS_CONTENT_TYPE_FIELD_TYPE, CpsDocumentMetadata } from '@cs-cms/types';
import type { FileWithPerFileMetadata } from '../../fieldInteractions/fieldInteractionsTypes';

enum GridFileFields {
  NAME = 'file.name',
  REMOVE = 'file.remove',
  METADATA_PREFIX = 'perFileMetadata.'
}

interface FormFileMetadataTableProps {
  show: boolean;
  files: FileWithPerFileMetadata[];
  perFileFields: ContentTypeFieldResponse[];
  formRenderProps: FormRenderProps;
  getPerFileFieldChoices: (fileKey: string, field: ContentTypeFieldResponse) => string[];
  isPerFileFieldEnabled: (fileKey: string, field: ContentTypeFieldResponse) => boolean;
  isPerFileFieldLoading: (fileKey: string, field: ContentTypeFieldResponse) => boolean;
  onPerFileFieldChange: (
    fileIndex: number,
    fieldName: string,
    value: CpsDocumentMetadata[string],
    files: FileWithPerFileMetadata[],
    formRenderProps: FormRenderProps
  ) => Promise<FileWithPerFileMetadata[]>;
  syncPerFileFieldInteractions: (files: FileWithPerFileMetadata[], formRenderProps: FormRenderProps) => Promise<void>;
  removePerFileFieldState: (fileKey: string) => void;
  onChange: (files: FileWithPerFileMetadata[]) => void;
  readOnly?: boolean;
}

export const FormFileMetadataTable: React.FC<FormFileMetadataTableProps> = ({
  show,
  files,
  perFileFields,
  formRenderProps,
  getPerFileFieldChoices,
  isPerFileFieldEnabled,
  isPerFileFieldLoading,
  onPerFileFieldChange,
  syncPerFileFieldInteractions,
  removePerFileFieldState,
  onChange,
  readOnly = false
}) => {
  const { selectedIndex } = useCurrentUserContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const { data: allFields } = useGetAllFields(selectedLocale, selectedIndex?.id);

  const edit: EditDescriptor = {};
  if (!readOnly) {
    files.forEach(fileItem => {
      edit[fileItem.file.name] = true;
    });
  }

  const handlersRef = React.useRef({
    perFileFields,
    getPerFileFieldChoices,
    isPerFileFieldEnabled,
    isPerFileFieldLoading,
    handleRemove: (_fileIndex: number, _fileKey: string) => {}
  });

  handlersRef.current = {
    perFileFields,
    getPerFileFieldChoices,
    isPerFileFieldEnabled,
    isPerFileFieldLoading,
    handleRemove: (fileIndex: number, fileKey: string) => {
      removePerFileFieldState(fileKey);
      const updatedFiles = files.filter((_, index) => index !== fileIndex);
      onChange(updatedFiles);
    }
  };

  const cellWrappersRef = React.useRef<{
    RemoveCellWrapper: ReturnType<typeof createRemoveCellWrapper>;
    CustomFieldCellWrapper: ReturnType<typeof createCustomFieldCellWrapper>;
  } | null>(null);

  if (cellWrappersRef.current === null) {
    cellWrappersRef.current = {
      RemoveCellWrapper: createRemoveCellWrapper(handlersRef),
      CustomFieldCellWrapper: createCustomFieldCellWrapper(handlersRef)
    };
  }

  const { RemoveCellWrapper, CustomFieldCellWrapper } = cellWrappersRef.current;

  const filesSignature = files.map(fileItem => fileItem.file.uid || fileItem.file.name).join('|');
  const formRenderPropsRef = React.useRef(formRenderProps);
  formRenderPropsRef.current = formRenderProps;
  const syncPerFileFieldInteractionsRef = React.useRef(syncPerFileFieldInteractions);
  syncPerFileFieldInteractionsRef.current = syncPerFileFieldInteractions;

  const lastSyncedSignatureRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!show || files.length === 0) {
      lastSyncedSignatureRef.current = null;
      return;
    }

    if (lastSyncedSignatureRef.current === filesSignature) {
      return;
    }

    lastSyncedSignatureRef.current = filesSignature;
    void syncPerFileFieldInteractionsRef.current(files, formRenderPropsRef.current);
  }, [show, files, filesSignature]);

  if (files.length === 0 || !show) {
    return null;
  }

  const handleItemChange = (event: GridItemChangeEvent) => {
    const { field, value, dataIndex } = event;

    if (!field || !field.startsWith(GridFileFields.METADATA_PREFIX)) {
      return;
    }

    const fieldName = field.replace(GridFileFields.METADATA_PREFIX, '');
    const updatedFiles = [...files];
    updatedFiles[dataIndex].perFileMetadata[fieldName] = value;

    void onPerFileFieldChange(dataIndex, fieldName, value, updatedFiles, formRenderProps).then(finalFiles => {
      onChange(finalFiles);
    });
  };

  const getEditorType = (field: ContentTypeFieldResponse) => {
    switch (field.type) {
      case CPS_CONTENT_TYPE_FIELD_TYPE.STRING:
        return 'text';
      case CPS_CONTENT_TYPE_FIELD_TYPE.INT:
      case CPS_CONTENT_TYPE_FIELD_TYPE.INT64:
      case CPS_CONTENT_TYPE_FIELD_TYPE.DECIMAL:
        return 'numeric';
      default:
        return undefined;
    }
  };

  return (
    <div>
      <Tooltip parentTitle>
        <Grid
          data={files}
          className="formMetadataGrid"
          resizable={true}
          onItemChange={handleItemChange}
          dataItemKey="file.name"
          edit={edit}
          editable={readOnly ? false : { mode: 'inline' }}
        >
          <GridColumn
            field={GridFileFields.NAME}
            title={translate('custom.toolbar.actions.form.fileName')}
            width={150}
            editable={false}
            cells={{ data: NameCell }}
          />
          {perFileFields.map(field => {
            const fieldTranslation = allFields?.find(x => x.name === field.name)?.displayName || field.name;
            const editorType = getEditorType(field);

            return (
              <GridColumn
                key={field.name}
                field={`${GridFileFields.METADATA_PREFIX}${field.name}`}
                title={fieldTranslation}
                minResizableWidth={100}
                className="formMetadataFieldCell"
                editor={editorType}
                cells={editorType ? undefined : { data: CustomFieldCellWrapper }}
              />
            );
          })}
          {readOnly ? null : (
            <GridColumn
              field={GridFileFields.REMOVE}
              title=""
              width={60}
              editable={false}
              cells={{ data: RemoveCellWrapper }}
            />
          )}
        </Grid>
      </Tooltip>
    </div>
  );
};

FormFileMetadataTable.displayName = 'FormFileMetadataTable';
